import { log } from "../core/logger";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { API_BASE_URL } from "./helpers";
import type { Amenity } from "../types/Amenity";
import type { Location } from "../types/Location";
import type { Notice } from "../types/Notice";
import type { PracticeManagerWithMember } from "../types/PracticeManager";
import type {
  BroadcastNoticeResponse,
  DeactivateLocationResponse,
  ILocationAPI,
  ShutterPreviewResponse,
  ShutterResponse,
} from "./locationApi";
import type { PaginatedResponse } from "../types/Pagination";

class RemoteLocationAPI implements ILocationAPI {
  clearCache(): void {
    // No caching implemented in remote API
    return;
  }

  async fetchLocationById(id: string): Promise<Location | null> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/locations/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`API error: ${response.statusText}`);
      }
      const json = await response.json();
      const location: Location = json.location;
      return location;
    } catch (error) {
      log("remoteLocationApi", "Failed to fetch location", error);
      throw error;
    }
  }

  async fetchSerializedLocations(includeInactive = false): Promise<PaginatedResponse<Location>> {
    try {
      const url = includeInactive
        ? `${API_BASE_URL}/api/locations?includeInactive=true`
        : `${API_BASE_URL}/api/locations`;
      const response = await fetchWithAuth(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const json = await response.json();

      // Backend returns { locations: [...] }, transform to PaginatedResponse
      const locations = json.locations || [];
      const result: PaginatedResponse<Location> = {
        success: true,
        data: locations,
        pagination: {
          total: locations.length,
          limit: locations.length,
          skip: 0,
          hasMore: false,
          returned: locations.length,
        },
      };
      return result;
    } catch (error) {
      log("remoteLocationApi", "Failed to fetch locations", error);
      throw error;
    }
  }

  async fetchPracticeManagerByLocation(locationId: string): Promise<Array<PracticeManagerWithMember>> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/locations/${locationId}/practice-managers`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const managers: Array<PracticeManagerWithMember> = await response.json();
      return managers;
    } catch (error) {
      log("remoteLocationApi", "Failed to fetch practice managers for location", error);
      throw error;
    }
  }

  async fetchAmenitiesForLocation(locationId: string): Promise<Array<Amenity>> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/locations/${locationId}/amenities`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const json = await response.json();
      const amenities: Array<Amenity> = json;
      return amenities;
    } catch (error) {
      log("remoteLocationApi", "Failed to fetch amenities for location", error);
      throw error;
    }
  }

  async fetchAmenityById(locationId: string, amenityId: string): Promise<Amenity | null> {
    try {
      const amenities = await this.fetchAmenitiesForLocation(locationId);
      const amenity = amenities.find((a) => a.id === amenityId) || null;
      return amenity;
    } catch (error) {
      log("remoteLocationApi", "Failed to fetch amenity by ID", error);
      throw error;
    }
  }

  async fetchPracticeManagedLocations(memberId: string): Promise<Location[]> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/members/${memberId}/practice-managed-locations`);
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const locations: Location[] = await response.json();
      return locations;
    } catch (error) {
      log("remoteLocationApi", "Failed to fetch practice managed locations for member", error);
      throw error;
    }
  }

  async fetchNotices(locationId: string): Promise<Notice[]> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/locations/${locationId}/notices`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const notices: Notice[] = await response.json();
      return notices;
    } catch (error) {
      log("remoteLocationApi", "Failed to fetch notices for location", error);
      throw error;
    }
  }

  async createNotice(locationId: string, message: string): Promise<Notice> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/locations/${locationId}/notices`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ message }),
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const json = await response.json();
      return json.notice;
    } catch (error) {
      log("remoteLocationApi", "Failed to create notice", error);
      throw error;
    }
  }

  async broadcastNotice(message: string, locationIds: string[]): Promise<BroadcastNoticeResponse> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/notices`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ message, locationIds }),
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const json = await response.json();
      return { success: json.success, count: json.count, notices: json.notices ?? [] };
    } catch (error) {
      log("remoteLocationApi", "Failed to broadcast notice", error);
      throw error;
    }
  }

  async deleteNotice(locationId: string, noticeId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/locations/${locationId}/notices/${noticeId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const json = await response.json();
      return { success: json.success, message: json.message };
    } catch (error) {
      log("remoteLocationApi", "Failed to delete notice", error);
      throw error;
    }
  }

  async previewShutterRooms(
    locationId: string,
    roomIds: string[],
    shutterUntil: string,
  ): Promise<ShutterPreviewResponse> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/locations/${locationId}/shutter/preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ roomIds, shutterUntil }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API error: ${response.statusText}`);
      }
      const json: ShutterPreviewResponse = await response.json();
      return json;
    } catch (error) {
      log("remoteLocationApi", "Failed to preview shutter rooms", error);
      throw error;
    }
  }

  async shutterRooms(locationId: string, roomIds: string[], shutterUntil: string): Promise<ShutterResponse> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/locations/${locationId}/shutter`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ roomIds, shutterUntil }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API error: ${response.statusText}`);
      }
      const json: ShutterResponse = await response.json();
      return json;
    } catch (error) {
      log("remoteLocationApi", "Failed to shutter rooms", error);
      throw error;
    }
  }

  async reopenRooms(locationId: string, roomIds: string[]): Promise<{ success: boolean; reopenedRooms: string[] }> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/locations/${locationId}/reopen`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ roomIds }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API error: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      log("remoteLocationApi", "Failed to reopen rooms", error);
      throw error;
    }
  }

  async deactivateLocation(locationId: string, confirmation: string): Promise<DeactivateLocationResponse> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/locations/${locationId}/deactivate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ confirmation }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API error: ${response.statusText}`);
      }
      const json: DeactivateLocationResponse = await response.json();
      return json;
    } catch (error) {
      log("remoteLocationApi", "Failed to deactivate location", error);
      throw error;
    }
  }
}

export default RemoteLocationAPI;
